import React from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import Breadcrumb from '../../../../components/Breadcrumb';
import { getAssessmentModuleCardWidthClass } from '../utils/getAssessmentModuleCardWidthClass';

/**
 * PatientOverviewSkeleton
 * -----------------------
 * The loading placeholder shown by PatientRouteWrapper while a patient's full
 * record is being fetched, in place of a bare spinner. It is a 1:1 structural
 * mirror of PatientOverview: it reuses the SAME container classes (the gray
 * header panel, the assessment-module card row, and the pre-assessment panel)
 * so the page keeps its exact shape and dimensions, and the swap to real
 * content is seamless rather than a layout jump.
 *
 * Design notes for future maintainers:
 *   - The container classNames here are copied verbatim from the real
 *     components (PatientInfoPanel, AssessmentModuleCard, and PatientOverview's
 *     own layout) so the placeholders match their size. We deliberately do NOT
 *     replicate every inner detail of those components — just enough shimmer
 *     blocks to suggest each container's content at the right height. If the
 *     real layout changes, mirror the container classes here too.
 *   - `summary` (optional) carries the incoming patient's name from the
 *     dashboard navigation (React Router location state). When present we render
 *     the REAL name immediately in the header — only the data we don't yet have
 *     shimmers. This is purely cosmetic and is never written into any data model.
 *   - `animate-pulse` is the shimmer (same convention as PatientFormTemplate).
 *   - `animate-fadeIn` is applied to the body (below the nav) only — NOT to
 *     Layout/TopNavBar — so the shared nav stays visually stable across the
 *     dashboard → skeleton → real-page transitions while the body gently reveals.
 *   - Static headings ("Assessment Modules", "Pre-Assessment Information") are
 *     shown as real text because they never change.
 */
function PatientOverviewSkeleton({ summary, moduleCount = 4, overviewPath }) {
  const { patientId } = useParams();

  const name = summary && (summary.firstName || summary.lastName)
    ? `${summary.firstName || ''} ${summary.lastName || ''}`.trim()
    : null;

  const breadcrumbTuples = [[
    'Patient Overview',
    overviewPath || `/patient/${patientId}`,
  ]];

  // A single shimmering grey block. `className` controls its size/shape.
  const Block = ({ className = '' }) => (
    <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
  );

  // One assessment module card placeholder. The OUTER container copies
  // AssessmentModuleCard's classes verbatim so the card footprint (padding,
  // border, radius, shadow, stretch height) matches exactly; the inner shimmer
  // blocks mirror that card's content rows (title, assigned-clinician badge,
  // progress bar, action button) so the height lines up with real cards.
  const ModuleCardSkeleton = () => (
    <div className="p-5 bg-white rounded-xl shadow border border-gray-200 flex flex-col h-full relative">
      <div className="flex-1 min-h-0 w-full">
        {/* Title */}
        <Block className="h-5 w-3/4 mb-2" />

        {/* Assigned clinician label + badge */}
        <Block className="h-4 w-28 mb-1.5" />
        <Block className="h-7 w-32 rounded-full mb-3" />
      </div>

      {/* Bottom footer — progress + action, aligned like real cards */}
      <div className="flex flex-col gap-1.5 w-full shrink-0 mt-3">
        <div className="self-stretch py-1.5 justify-start items-center gap-3 inline-flex">
          <div className="flex-grow h-2 rounded-lg bg-gray-200 animate-pulse" />
          <Block className="h-4 w-8" />
        </div>
        <Block className="h-10 w-full rounded-lg" />
      </div>
    </div>
  );

  return (
    <Layout activePage="dashboard">
      {/* Breadcrumb row — same wrapper AND the real Breadcrumb as PatientOverview,
          so it sits in the identical position and doesn't move on the swap. */}
      <div className="flex items-center justify-between mb-4 animate-fadeIn">
        <Breadcrumb tupleList={breadcrumbTuples} />
      </div>

      <div className="flex-grow pl-8 pr-8 animate-fadeIn">
        {/* Header panel — container classes copied verbatim from PatientInfoPanel */}
        <div className="w-full px-6 py-5 bg-gray-50 rounded-xl border border-gray-200 justify-start items-center gap-14 inline-flex text-left">
          <div className="grow shrink basis-0 h-16 justify-start items-center gap-5 flex">
            <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
              {/* Name: show the real incoming name when we have it, else shimmer.
                  Matches PatientInfoPanel's name typography exactly. */}
              {name ? (
                <div className="self-stretch text-gray-900 text-3xl font-semibold leading-9">
                  {name}
                </div>
              ) : (
                /* h-9 matches the real name's leading-9 (36px); the parent's
                   gap-1 provides the space to the email line below. */
                <Block className="h-9 w-64" />
              )}
              {/* Email line — h-6 matches text-base leading-normal (~24px). No
                  mt here: the parent flex-col's gap-1 already spaces it from the
                  name, exactly as in PatientInfoPanel. */}
              <Block className="h-6 w-48" />
            </div>
          </div>
          {/* Right-hand action buttons (View Actions + Go to Documents) */}
          <div className="flex justify-start items-center gap-3">
            <Block className="h-10 w-32 rounded-lg" />
            <Block className="h-10 w-44 rounded-lg" />
          </div>
        </div>

        {/* Assessment Modules */}
        <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">
          Assessment Modules
        </div>
        {/* Module row — same flex wrapper and per-card responsive widths as PatientOverview */}
        <div className="w-full justify-between items-stretch gap-1 inline-flex">
          {Array.from({ length: moduleCount }, (_, i) => i).map((i) => (
            <div key={i} className={getAssessmentModuleCardWidthClass(moduleCount)}>
              <ModuleCardSkeleton />
            </div>
          ))}
        </div>

        {/* Pre-Assessment Information */}
        <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">
          Pre-Assessment Information
        </div>
        {/* Generic panel placeholder sized like the pre-assessment container */}
        <div className="w-full px-6 py-5 bg-white rounded-xl border border-gray-200 space-y-3">
          <Block className="h-4 w-1/2" />
          <Block className="h-4 w-3/4" />
          <Block className="h-4 w-2/3" />
        </div>
      </div>
    </Layout>
  );
}

export default PatientOverviewSkeleton;
